/** Money is minor units everywhere; formatting happens only at the edge. §107 */

const SYMBOLS: Record<string, string> = { ILS: '₪', USD: '$', EUR: '€', GBP: '£' };
const EXPONENTS: Record<string, number> = { ILS: 2, USD: 2, EUR: 2, GBP: 2, JPY: 0 };

export interface MoneyOptions {
  currency?: string;
  /** Drop the agorot when the value is round — cleaner big numbers. §5 */
  compactDecimals?: boolean;
  signDisplay?: 'auto' | 'never' | 'always' | 'exceptZero';
}

export function formatMoney(minor: number, options: MoneyOptions = {}): string {
  const currency = options.currency ?? 'ILS';
  const exponent = EXPONENTS[currency] ?? 2;
  const value = minor / 10 ** exponent;
  const symbol = SYMBOLS[currency] ?? currency;

  const hasFraction = Math.abs(minor) % 10 ** exponent !== 0;
  const digits = options.compactDecimals === false ? exponent : hasFraction ? exponent : 0;

  const body = new Intl.NumberFormat('he-IL', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  }).format(Math.abs(value));

  const sign =
    options.signDisplay === 'never' ? ''
    : minor < 0 ? '−'
    : options.signDisplay === 'always' || options.signDisplay === 'exceptZero' ? (minor === 0 ? '' : '+')
    : '';

  return `${sign}${symbol}${body}`;
}

/** Short form for charts and dense rows: ₪12.8K */
export function formatMoneyShort(minor: number, currency = 'ILS'): string {
  const value = Math.abs(minor) / 100;
  const symbol = SYMBOLS[currency] ?? currency;
  const sign = minor < 0 ? '−' : '';
  if (value >= 1_000_000) return `${sign}${symbol}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${sign}${symbol}${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K`;
  return `${sign}${symbol}${Math.round(value)}`;
}

export function formatPercent(ratio: number | null, digits = 0): string {
  if (ratio === null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(digits)}%`;
}

const HE_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const HE_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

export function monthName(isoOrMonth: string): string {
  const month = Number(isoOrMonth.slice(5, 7));
  return HE_MONTHS[month - 1] ?? '';
}

/** "היום" / "אתמול" / "יום שלישי" / "12 בספטמבר" — the timeline's day headings. §18 */
export function dayHeading(iso: string, today: string): string {
  const diff = daysBetween(iso, today);
  if (diff === 0) return 'היום';
  if (diff === 1) return 'אתמול';
  if (diff > 1 && diff < 7) return `יום ${HE_DAYS[new Date(`${iso}T00:00:00Z`).getUTCDay()]}`;
  const day = Number(iso.slice(8, 10));
  return `${day} ב${monthName(iso)}`;
}

export function formatDayMonth(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

export function formatTime(epochMs: number, timeZone = 'Asia/Jerusalem'): string {
  return new Intl.DateTimeFormat('he-IL', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone })
    .format(new Date(epochMs));
}

export function formatFullDate(epochMs: number, timeZone = 'Asia/Jerusalem'): string {
  return new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'long', year: 'numeric', timeZone })
    .format(new Date(epochMs));
}

export function toIso(epochMs: number, timeZone = 'Asia/Jerusalem'): string {
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone })
    .format(new Date(epochMs));
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export function pluralDays(n: number): string {
  if (n === 1) return 'יום';
  if (n === 2) return 'יומיים';
  return `${n} ימים`;
}

export function pluralMonths(n: number): string {
  if (n === 1) return 'חודש';
  if (n === 2) return 'חודשיים';
  return `${n} חודשים`;
}
